import { useCallback } from 'react';
import { Timer } from '../../components/Timer';
import useGameContext from '../../context/GameContext';
import usePlayerContext from '../../context/PlayerContext';
import { Layout } from '../../layouts/Layout';
import { ROUTES } from '../../routes';
import { useSearchNavigate } from '../../hooks/useSearchNavigate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { TIME_TO_ANSWER, useQuestionFlow } from '../../hooks/useQuestionFlow';
import { QuestionLayout } from '../Question/QuestionLayout';

export const SurveyQuestion = () => {
  const {
    currentSurveyQuestion,
    currentSurveyQuestionIndex,
    questionCount,
    surveyQuestionCount,
    surveyQuestionsLoaded,
    gameSession,
    updateSurveyAnswers,
    setCurrentSurveyQuestionIndex,
  } = useGameContext();

  const navigate = useSearchNavigate();
  const { player } = usePlayerContext();
  const { onResult } = useAnalytics();

  const onComplete = useCallback(() => {
    onResult();
    navigate(ROUTES.Result);
  }, [navigate, onResult]);

  const onNotReady = useCallback(() => {
    navigate(ROUTES.Result);
    // Previous flow: return to the survey invitation when survey questions were unavailable.
    // navigate(ROUTES.SurveyInvite);
  }, [navigate]);

  const {
    loadingNewQuestion,
    onSelectedAnswer,
    skipQuestion,
    currentAnsweredQuestion,
    hasNextQuestion,
    questionStartDate,
    isRenderable,
    endFlow,
  } = useQuestionFlow({
    currentQuestion: currentSurveyQuestion,
    currentIndex: currentSurveyQuestionIndex,
    questionCount: surveyQuestionCount,
    setCurrentIndex: setCurrentSurveyQuestionIndex,
    updateAnswer: (answerId) => updateSurveyAnswers(answerId),
    onComplete,
    gameSession,
    resetIndexOnMount: true,
    isReady: surveyQuestionsLoaded,
    onNotReady,
  });

  const onEndGame = useCallback(() => {
    endFlow();
    onComplete();
  }, [endFlow, onComplete]);

  const playerName = `${player?.first_name} ${player?.last_name}`;
  const points = gameSession?.points || 0;
  const totalQuestionCount = questionCount + surveyQuestionCount;

  if (!isRenderable) {
    return null;
  }

  return (
    <Layout>
      <QuestionLayout
        mode="survey"
        question={currentSurveyQuestion}
        currentIndex={questionCount + currentSurveyQuestionIndex}
        questionCount={totalQuestionCount}
        playerName={playerName}
        points={points}
        loadingNewQuestion={loadingNewQuestion}
        hasNextQuestion={hasNextQuestion}
        selectedAnswerId={currentAnsweredQuestion?.answerId}
        correctAnswerId={currentAnsweredQuestion?.correctAnswerId}
        onSelectAnswer={onSelectedAnswer}
        onSkipQuestion={skipQuestion}
        onEndGame={onEndGame}
        timerSlot={
          <Timer
            startDate={questionStartDate}
            seconds={TIME_TO_ANSWER}
            paused={loadingNewQuestion}
          />
        }
      />
    </Layout>
  );
};
